import {ProvisionRunStatus} from './provision-run-status.enum';

/**
 * German presentation companion of {@link ProvisionRunStatus} — the SINGLE place in the cp frontend
 * that NAMES a run status, so the step bar (CpProvisionRunStatusFlowComponent), the run-detail
 * header (CpProvisionRunDetailComponent) and the dashboard start page (via
 * `model/provision-run-status-presentation.ts`, which imports this map) can never disagree about
 * what a run currently is.
 *
 * Lives next to `provision-run-status-order.ts` for the same reason that file does: both are FE
 * companions of the frozen backend enum, neither is an enum itself, and keeping them together makes
 * it obvious that the ORDER (business progress) and the LABEL (what the operator reads) are two
 * separate statements about the same ordinal — see 04-frontend.md §5.2.
 *
 * The maps are keyed by the enum member, NOT by an ordinal literal: a map written with numeric keys
 * would silently survive a backend reordering, which is exactly the failure mode
 * 02-datenmodell.md §0.5 exists to prevent.
 *
 * ── ONE LABEL MAP, NOT TWO
 *
 * A second German label table used to live in `model/provision-run-status-presentation.ts` and
 * CONTRADICTED this one on the three job-driven statuses. Two tables that name the same ordinal
 * differently are not a style question: the run-detail header read one of them and the dashboard
 * runs table read the other, so the SAME run was called two different things on two screens of the
 * same module. The presentation table now imports {@link CP_RUN_STATUS_LABELS} and only adds icon
 * and colour, which are its own subject.
 *
 * ── WHY "Berechnung läuft" AND NOT "In Berechnung" (04-frontend.md §5.1)
 *
 * The merge kept the wording of the presentation table, because that is the one the step bar of
 * §5.1 needs. §5.1 renders a failed run as an error badge ON the step it interrupted, next to the
 * label of the status the run rests on — so both halves of one phase are read side by side, in one
 * table row, and they have to be the same sentence pattern:
 *
 *     Berechnung läuft   ←→  Berechnung fehlgeschlagen
 *     Aktivierung läuft  ←→  Aktivierung fehlgeschlagen
 *     Auszahlung läuft   ←→  Auszahlung fehlgeschlagen
 *
 * "In Berechnung" next to "Berechnung fehlgeschlagen" mixes a prepositional phrase with a
 * participle clause and reads as two unrelated vocabularies in one row.
 *
 * The second reason is that these three statuses — and ONLY these three — are the ones a Spring
 * Batch job rests on ({@link CP_RUN_STATUS_PROCESSING}, the auto refresh of §5.1): "läuft" is
 * literally what the spinner rendered next to them says. IN_REVIEW is deliberately NOT one of them:
 * it is a resting state an operator deliberately put the run into, no job is running, so it keeps
 * the prepositional "In Prüfung".
 */
export const CP_RUN_STATUS_LABELS: Readonly<Record<ProvisionRunStatus, string>> = {
    [ProvisionRunStatus.NEW]: 'Neu',
    [ProvisionRunStatus.IN_CALCULATION]: 'Berechnung läuft',
    [ProvisionRunStatus.CALCULATED]: 'Berechnet',
    [ProvisionRunStatus.IN_REVIEW]: 'In Prüfung',
    [ProvisionRunStatus.IN_ACTIVATION]: 'Aktivierung läuft',
    [ProvisionRunStatus.ACTIVATED]: 'Aktiviert',
    [ProvisionRunStatus.IN_PAYOUT]: 'Auszahlung läuft',
    [ProvisionRunStatus.PAID_OUT]: 'Ausgezahlt',
    [ProvisionRunStatus.COMPLETED]: 'Abgeschlossen',
    [ProvisionRunStatus.CALCULATION_FAILED]: 'Berechnung fehlgeschlagen',
    [ProvisionRunStatus.ACTIVATION_FAILED]: 'Aktivierung fehlgeschlagen',
    [ProvisionRunStatus.PAYOUT_FAILED]: 'Auszahlung fehlgeschlagen',
    [ProvisionRunStatus.ABORTED]: 'Abgebrochen'
};

/**
 * What an UNMAPPED status is called. Exported because the presentation table needs the same word
 * for its own fallback entry — the one literal that would otherwise be duplicated again.
 */
export const CP_RUN_STATUS_LABEL_UNKNOWN: string = 'Unbekannt';

/** PrimeNG `p-tag` severities, mirroring the label map one-to-one. */
export type CpRunStatusSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

export const CP_RUN_STATUS_SEVERITIES: Readonly<Record<ProvisionRunStatus, CpRunStatusSeverity>> = {
    [ProvisionRunStatus.NEW]: 'secondary',
    [ProvisionRunStatus.IN_CALCULATION]: 'info',
    [ProvisionRunStatus.CALCULATED]: 'info',
    [ProvisionRunStatus.IN_REVIEW]: 'warn',
    [ProvisionRunStatus.IN_ACTIVATION]: 'info',
    [ProvisionRunStatus.ACTIVATED]: 'info',
    [ProvisionRunStatus.IN_PAYOUT]: 'info',
    [ProvisionRunStatus.PAID_OUT]: 'success',
    [ProvisionRunStatus.COMPLETED]: 'success',
    [ProvisionRunStatus.CALCULATION_FAILED]: 'danger',
    [ProvisionRunStatus.ACTIVATION_FAILED]: 'danger',
    [ProvisionRunStatus.PAYOUT_FAILED]: 'danger',
    [ProvisionRunStatus.ABORTED]: 'danger'
};

/**
 * The transient statuses a background job rests on. Used to render a spinner instead of a static
 * tag: while a run is IN_* the displayed status is a snapshot of something still moving, and the
 * step bar's auto refresh (running -> not-running of the jb indicator) is what ends it.
 *
 * This list is also the reason the three labels above end in "läuft" — see the file TSDoc.
 */
export const CP_RUN_STATUS_PROCESSING: readonly ProvisionRunStatus[] = [
    ProvisionRunStatus.IN_CALCULATION,
    ProvisionRunStatus.IN_ACTIVATION,
    ProvisionRunStatus.IN_PAYOUT
];

/** German label of a run status; falls back to a readable placeholder for an unmapped value. */
export function getProvisionRunStatusLabel(status: ProvisionRunStatus): string {
    return CP_RUN_STATUS_LABELS[status] ?? CP_RUN_STATUS_LABEL_UNKNOWN;
}

/** PrimeNG tag severity of a run status. */
export function getProvisionRunStatusSeverity(status: ProvisionRunStatus): CpRunStatusSeverity {
    return CP_RUN_STATUS_SEVERITIES[status] ?? 'secondary';
}

/** True while the run rests on a transient IN_* status, i.e. a background job is producing data. */
export function isProvisionRunProcessing(status: ProvisionRunStatus): boolean {
    return CP_RUN_STATUS_PROCESSING.includes(status);
}
